import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navbar from './components/navigation/Navbar';
import Footer from './components/navigation/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import TestPage from './pages/TestPage';
import PhoneAuth from './pages/Auth/PhoneAuth';
import ProfileComplete from './pages/Auth/ProfileComplete';
import Dashboard from './pages/Dashboard';
import ReadingForm from './pages/Reading/ReadingForm';
import ReadingResult from './pages/Reading/ReadingResult';
import ShareUnlock from './pages/ShareUnlock';
import Premium from './pages/Premium';
import PaymentSuccess from './pages/Payment/Success';
import Profile from './pages/Profile';
import ReadingHistory from './pages/ReadingHistory';
import Terms from './pages/Legal/Terms';
import Privacy from './pages/Legal/Privacy';
import ShippingReturns from './pages/Legal/ShippingReturns';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { initGA, trackPageView, trackReferralTraffic, trackUserAcquisition } from './lib/analytics';

// Initialize Google Analytics
initGA();

// Track page views and UTM parameters
const PageViewTracker: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  
  useEffect(() => {
    // Track page view
    trackPageView(location.pathname + location.search);
    
    // Check for UTM source parameter and track referral traffic
    const urlParams = new URLSearchParams(location.search);
    const utmSource = urlParams.get('utm_source');
    
    if (utmSource) {
      // Map minimal UTM source back to full name for tracking
      const sourceMap: Record<string, string> = {
        'wa': 'whatsapp',
        'whatsapp': 'whatsapp'
      };
      
      const fullSource = sourceMap[utmSource] || utmSource;
      
      // Track referral traffic from shared links (simplified)
      trackReferralTraffic(fullSource, 'social', 'user_share');
      
      // If this is a WhatsApp referral, track it specifically
      if (fullSource === 'whatsapp') {
        trackUserAcquisition('whatsapp_share', 'user_referral');
      }
    }
  }, [location]);
  
  return <>{children}</>;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, userProfile, loading } = useAuth();

  console.log('ProtectedRoute - Auth State:', {
    user: !!user,
    userPhone: user?.phoneNumber,
    userProfile: !!userProfile,
    profileName: userProfile?.name,
    profileBirthDate: userProfile?.birth_date,
    profileBirthTime: userProfile?.birth_time,
    profileBirthLocation: userProfile?.birth_location,
    loading
  });

  if (loading) {
    console.log('ProtectedRoute - Loading state');
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-lg font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    console.log('ProtectedRoute - No user, redirecting to auth');
    return <Navigate to="/auth" replace />;
  }
  
  // Check if profile is incomplete - redirect to profile completion
  const isProfileIncomplete = !userProfile || 
    !userProfile.name || 
    !userProfile.birth_date || 
    !userProfile.birth_time || 
    !userProfile.birth_location?.city;

  if (isProfileIncomplete) {
    console.log('ProtectedRoute - Profile incomplete, redirecting to profile completion');
    return <Navigate to="/profile/complete" replace />;
  }

  console.log('ProtectedRoute - All checks passed, rendering children');
  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  const { user, loading } = useAuth();

  console.log('AppRoutes - Auth State:', {
    user: !!user,
    userPhone: user?.phoneNumber,
    loading
  });

  if (loading) {
    console.log('AppRoutes - Loading state');
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-lg font-medium">Initializing...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <PageViewTracker>
        <div className="flex flex-col min-h-screen">
          {user && <Navbar />}
          <main className="flex-grow">
            <ErrorBoundary fallbackTitle="Page Error">
              <Routes>
                {/* Test Route - Remove after debugging */}
                <Route path="/test" element={<TestPage />} />
                
                {/* Public Routes */}
                <Route path="/auth" element={!user ? <PhoneAuth /> : <Navigate to="/" replace />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/shipping-returns" element={<ShippingReturns />} />
                
                {/* Protected Routes */}
                <Route path="/profile/complete" element={
                  <ErrorBoundary fallbackTitle="Profile Completion Error">
                    <ProfileComplete />
                  </ErrorBoundary>
                } />
                
                <Route path="/" element={
                  <ErrorBoundary fallbackTitle="Dashboard Error">
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  </ErrorBoundary>
                } />
                
                <Route path="/share-unlock" element={
                  <ErrorBoundary fallbackTitle="Share Unlock Error">
                    <ProtectedRoute>
                      <ShareUnlock />
                    </ProtectedRoute>
                  </ErrorBoundary>
                } />
                
                <Route path="/reading/:type" element={
                  <ErrorBoundary fallbackTitle="Reading Form Error">
                    <ProtectedRoute>
                      <ReadingForm />
                    </ProtectedRoute>
                  </ErrorBoundary>
                } />
                
                <Route path="/reading/result/:id" element={
                  <ErrorBoundary fallbackTitle="Reading Result Error">
                    <ProtectedRoute>
                      <ReadingResult />
                    </ProtectedRoute>
                  </ErrorBoundary>
                } />
                
                <Route path="/premium" element={
                  <ErrorBoundary fallbackTitle="Premium Page Error">
                    <ProtectedRoute>
                      <Premium />
                    </ProtectedRoute>
                  </ErrorBoundary>
                } />

                <Route path="/payment/success" element={
                  <ErrorBoundary fallbackTitle="Payment Success Error">
                    <ProtectedRoute>
                      <PaymentSuccess />
                    </ProtectedRoute>
                  </ErrorBoundary>
                } />
                
                <Route path="/profile" element={
                  <ErrorBoundary fallbackTitle="Profile Page Error">
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  </ErrorBoundary>
                } />
                
                <Route path="/readings" element={
                  <ErrorBoundary fallbackTitle="Reading History Error">
                    <ProtectedRoute>
                      <ReadingHistory />
                    </ProtectedRoute>
                  </ErrorBoundary>
                } />
                
                {/* Fallback route */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </ErrorBoundary>
          </main>
          <Footer />
          <ToastContainer 
            position="bottom-center"
            autoClose={3000}
            hideProgressBar
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
            className="sm:max-w-sm mx-auto"
          />
        </div>
      </PageViewTracker>
    </Router>
  );
};

function App() {
  return (
    <ErrorBoundary fallbackTitle="Application Error">
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;